import { useEffect, useState, MouseEvent } from "react";
import { useShallow } from "zustand/react/shallow";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RestaurantIcon from '@mui/icons-material/Restaurant';

import type { IListMenuItem, IListMenuOutput, IGetMenuRecipeItem } from "~/providers/interfaces";
import { parseBeDate, dateToBe, getShortDateTitle } from "~/utils/date";
import { getImagePath } from "~/utils/utils";
import { MS_PER_DAY } from "~/const";
import { userStore } from "~/stores/user";
import { getMenu, regenerateMeal } from "~/providers/api";
import { notificationStore } from "~/stores/notification";
import NutritionInfo from "~/components/NutritionInfo";
import Difficulty from "~/components/Difficulty";
import ConfirmModal from "~/modals/ConfirmModal";

import "./style.less";
import { trans } from "~/utils/trans";

interface IState {
	recipes: Array<IGetMenuRecipeItem>;
	// jidlo cekajici na potvrzeni vymeny (ma zbytky v dalsich dnech)
	confirmItem: IListMenuItem | null;
	// id_food_tag prave vymenovaneho jidla
	regenerating: number | null;
}

interface IMeals {
	list: Array<IListMenuItem>;
	date: string;
	// jidelnicky vsech dnu, pro spocitani na kolik dni se viceporcovy recept vari
	allMenus?: Array<IListMenuOutput>;
	openRecipe?: (item: IGetMenuRecipeItem) => void;
	// po vymene jidla je potreba znovu nacist jidelnicky
	onRegenerated?: () => void;
	// center = vystredene (desktop), list = zarovnane vlevo s fotkou (telefon)
	variant?: "center" | "list";
}

const DAYS_IN_WEEK = 7;

// nazev receptu bez posledniho slova (vcetne mezery) a samotne posledni slovo
function getNameStart(name: string) {
	return name.slice(0, name.trimEnd().lastIndexOf(" ") + 1);
}

function getNameEnd(name: string) {
	return name.trimEnd().slice(name.trimEnd().lastIndexOf(" ") + 1);
}

export default function Meals({
	list = [],
	date = "",
	allMenus = [],
	openRecipe = () => {},
	onRegenerated = () => {},
	variant = "center",
} : IMeals) {
	const { userData } = userStore(useShallow(userState => ({
		userData: userState.user,
	})));
	const setNotification = notificationStore.getState().setNotification;
	const [state, setState] = useState<IState>({
		recipes: [],
		confirmItem: null,
		regenerating: null,
	});
	// jidla v minulosti se vymenit nedaji
	const canRegenerate = date >= dateToBe(new Date());

	function onClick(recipeId: number) {
		const filtered = state.recipes.filter(item => item.id_recipe === recipeId);

		if (filtered.length === 1) {
			openRecipe(filtered[0]);
		} else {
			setNotification(trans("notifications.schedule.recipeNotFound"), "error");
		}
	}

	// slozitost neni v seznamu jidel, bereme ji z nactenych receptu
	function getDifficulty(recipeId: number) {
		return state.recipes.find(item => item.id_recipe === recipeId)?.difficulty || 0;
	}

	// fotka je take jen v nactenych receptech
	function getPhoto(recipeId: number) {
		return state.recipes.find(item => item.id_recipe === recipeId)?.image_path || "";
	}

	// dny se zbytky z jidla uvareneho v tento den
	function getLeftoverDates(item: IListMenuItem) {
		return allMenus.filter(day => day.menu.some(menuItem => menuItem.cooked_date === date
			&& menuItem.id_recipe === item.id_recipe
			&& menuItem.id_food_tag === item.id_food_tag)).map(day => day.date);
	}

	// zbytek: "Zbytek z pondělí", den vareni viceporcoveho receptu: "Uvařte na 3 dny", jinak ""
	function getLeftoverText(item: IListMenuItem) {
		if (item.cooked_date) {
			const cooked = parseBeDate(item.cooked_date);
			const daysBack = Math.round((parseBeDate(date).getTime() - cooked.getTime()) / MS_PER_DAY);

			return daysBack < DAYS_IN_WEEK
				// day podle getDay() (0 = nedele)
				? trans("meals.leftoverFromDay", { day: cooked.getDay() })
				: trans("meals.leftoverFromDate", { date: getShortDateTitle(cooked) });
		}

		const leftoversCount = getLeftoverDates(item).length;

		return leftoversCount > 0
			? trans("meals.cookFor", { count: leftoversCount + 1 })
			: "";
	}

	// "úterý 25. 10." / "Tuesday 25/10" pro kazdy den se zbytkem
	function getLeftoverDayTitles(item: IListMenuItem) {
		return getLeftoverDates(item).map(beDate => getShortDateTitle(parseBeDate(beDate), true));
	}

	async function loadData() {
		const menuData = await getMenu(userData.jwt, userData.id, date);

		if (menuData.noErrors) {
			setState(prev => ({
				...prev,
				recipes: menuData.data.recipes,
			}));
		} else {
			setNotification(menuData.errors.join(", "), "error");
		}
	}

	async function doRegenerate(item: IListMenuItem) {
		setState(prev => ({ ...prev, confirmItem: null, regenerating: item.id_food_tag }));

		const regenerateData = await regenerateMeal(userData.jwt, userData.id, date, item.id_food_tag);

		setState(prev => ({ ...prev, regenerating: null }));

		if (regenerateData.noErrors) {
			setNotification(trans("notifications.schedule.mealRegenerated"));
			onRegenerated();
			loadData();
		} else {
			setNotification(regenerateData.errors.join(", "), "error");
		}
	}

	function clickRegenerate(event: MouseEvent, item: IListMenuItem) {
		// kliknuti na jidlo otevira recept
		event.stopPropagation();

		if (state.regenerating !== null) {
			return;
		}

		if (getLeftoverDates(item).length > 0) {
			setState(prev => ({ ...prev, confirmItem: item }));
		} else {
			doRegenerate(item);
		}
	}

	useEffect(() => {
		loadData();
	}, [date]);

	function getRegenerateButton(item: IListMenuItem) {
		return <button
			type="button"
			className={`mealsContent__regenerate ${state.regenerating === item.id_food_tag ? "mealsContent__regenerate--active" : ""}`}
			title={trans("meals.regenerate")}
			aria-label={trans("meals.regenerate")}
			onClick={event => clickRegenerate(event, item)}
		>
			<AutorenewIcon />
		</button>;
	}

	// cepice slozitosti za nazvem receptu
	function getDifficultyBadge(item: IListMenuItem) {
		return <span className="mealsContent__difficulty">
			<Difficulty value={getDifficulty(item.id_recipe)} size="small" />
		</span>;
	}

	const isList = variant === "list";

	return <div className={`mealsContent ${isList ? "mealsContent--list" : ""}`}>
		<h3>{trans("meals.title")}</h3>
		{ list.length === 0
			? <p>{trans("meals.empty")}</p>
			: <ul>
				{list.map(item => <li key={item.id_recipe} onClick={() => onClick(item.id_recipe)}>
					{ isList && <div className="mealsContent__photo">
						{ getPhoto(item.id_recipe)
							? <img src={getImagePath(getPhoto(item.id_recipe))} alt="" loading="lazy" />
							: <RestaurantIcon /> }
					</div> }
					<div className="mealsContent__text">
						<h4>{item.food_name}</h4>
						{ getLeftoverText(item) && <div className="mealsContent__leftover">{getLeftoverText(item)}</div> }
						<p>
							{getNameStart(item.name)}
							{/* posledni slovo, slozitost a ikona se nesmi rozdelit na dva radky */}
							<span className="mealsContent__nameEnd">
								{getNameEnd(item.name)}
								{ getDifficulty(item.id_recipe) > 0 && <>&nbsp;{getDifficultyBadge(item)}</> }
								{ canRegenerate && !isList && getRegenerateButton(item) }
							</span>
						</p>
						<NutritionInfo energy={item.energy} proteins={item.proteins} carbohydrates={item.carbohydrates} fats={item.fats} />
					</div>
					{/* v seznamu je vymena samostatne vpravo, s plochou pro prst */}
					{ canRegenerate && isList && getRegenerateButton(item) }
				</li>)}
			</ul>
		}
		{ state.confirmItem && <ConfirmModal
			title={trans("meals.confirmTitle")}
			warning={true}
			confirmLabel={trans("meals.confirmLabel")}
			onConfirm={() => doRegenerate(state.confirmItem)}
			onClose={() => setState(prev => ({ ...prev, confirmItem: null }))}
		>
			<p>
				<strong>{state.confirmItem.name}</strong> {trans("meals.confirmText", { count: getLeftoverDates(state.confirmItem).length + 1 })}
			</p>
			<ul className="modal-confirm__chips">
				{getLeftoverDayTitles(state.confirmItem).map(dayTitle => <li key={dayTitle}>{dayTitle}</li>)}
			</ul>
			<p className="modal-confirm__note">
				{trans("meals.confirmNote")}
			</p>
		</ConfirmModal> }
	</div>;
}
